import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ApproveIncomingRequests,
  GetIncomingHGOFilteredRequests,
  GetIncomingHGORequests,
  GetIncomingMonazamFilteredRequests,
  GetIncomingMonazamRequests,
  RejectIncomingRequests,
} from "../../../../../Actions/RequestActions";
import { formatAmount, SuccessToast } from "../../../../../Util";
import { Spinner } from "react-bootstrap";
import "./Table.css";

const formatTimeTo12Hour = (time24) => {
  if (!time24) return '';
  
  try {
    const timeParts = time24.split(':');
    if (timeParts.length < 2) return time24;
    
    const hours = parseInt(timeParts[0], 10);
    const minutes = timeParts[1];
    const seconds = timeParts[2] || '';
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    return `${hours12.toString().padStart(2, '0')}:${minutes} ${period}`;
  } catch (error) {
    return time24;
  }
};

const Table = ({ reload, filters, setTotalPages, page, setPage }) => {
  const { userType } = useParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRequests = () => {
    setLoading(true);
    setError("");
    if (userType === "monazam") {
      GetIncomingMonazamRequests(
        setRequests,
        setLoading,
        setError,
        page,
        setTotalPages
      );
    } else if (userType === "hgo") {
      GetIncomingHGORequests(
        setRequests,
        setLoading,
        setError,
        page,
        setTotalPages
      );
    }
  };

  const fetchFilteredRequests = () => {
    setLoading(true);
    setError("");
    if (userType === "monazam") {
      GetIncomingMonazamFilteredRequests(
        filters,
        setRequests,
        setLoading,
        setError,
        1,
        setTotalPages
      );
    } else if (userType === "hgo") {
      GetIncomingHGOFilteredRequests(
        filters,
        setRequests,
        setLoading,
        setError,
        1,
        setTotalPages
      );
    }
    setPage(1);
  };

  const handleApprove = () => {
    SuccessToast("Request approved successfully.");
    fetchRequests();
  };

  const handleReject = () => {
    SuccessToast("Request rejected successfully.");
    fetchRequests();
  };

  const approveRequest = (request) => {
    ApproveIncomingRequests(request.id, setError, handleApprove);
  };

  const rejectRequest = (request) => {
    RejectIncomingRequests(request.id, setError, handleReject);
  };

  useEffect(() => {
    if (!filters.date && !filters.time && !filters.currency) {
      fetchRequests();
    } else {
      fetchFilteredRequests();
    }
  }, [reload, userType, filters, page]);

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center loading-container">
        <Spinner animation="border" className="spinner-green" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  else
    return (
      <div>
        {!!error && (
          <div className="alert alert-warning mb-3" role="alert">
            <small>⚠️ {error}</small>
          </div>
        )}
        {!error && (!requests || !Array.isArray(requests) || requests.length === 0) ? (
          <div className="d-flex justify-content-center align-items-center loading-container">
            <div className="text-center">
              <p className="text-muted">No data available</p>
            </div>
          </div>
        ) : !error && (
          <div className="table-responsive">
            <table className={`table ${userType === "monazam" ? "incoming-table" : "incoming-table incoming-table-hgo"}`}>
            <thead>
              <tr>
                {userType === "monazam" && <th className="tableheader-txt">HGO Name</th>}
                <th className="tableheader-txt">Date / Time</th>
                <th className="tableheader-txt">Hajji Name</th>
                <th className="tableheader-txt">Monazam Bank</th>
                <th className="tableheader-txt">Transaction ID</th>
                <th className="tableheader-txt">Narration</th>
                <th className="tableheader-txt">Currency</th>
                <th className="tableheader-txt">Amount</th>
                {userType === "monazam" && (
                  <th className="tableheader-txt">Actions</th>
                )}
                {userType === "hgo" && (
                  <th className="tableheader-txt">Status</th>
                )}
              </tr>
            </thead>
            <tbody>
              {(requests && Array.isArray(requests) ? requests : []).map((request, index) => (
              <tr key={index}>
                {userType === "monazam" && (
                  <td className="tabledata-txt">{request.hgoUser?.name || 'Name ' + (index + 1)}</td>
                )}
                <td className="tabledata-txt">
                  <div className="date-time-cell">
                    <span>{new Date(request.date).toLocaleDateString('en-GB')}</span>
                    {request.time && (
                      <span className="time">{formatTimeTo12Hour(request.time)}</span>
                    )}
                  </div>
                </td>
                <td className="tabledata-txt">{request.hajiName || 'N/A'}</td>
                <td className="tabledata-txt">{request.bankName ? `${request.bankName}, ${request.iban || 'PK40MEZN0000001123456702'}` : 'N/A'}</td>
                <td className="tabledata-txt">{request.transactionId || 'N/A'}</td>
                <td className="tabledata-txt">{request.narration}</td>
                <td className="tabledata-txt">{request.currency}</td>
                <td className="tabledata-txt">
                  {formatAmount(request.amount)}
                </td>
                {userType === "monazam" && request.status === "pending" ? (
                  <td className="tabledata-txt">
                    <div className="actions-cell">
                      <button
                        className="btn btn-sm btn-approved"
                        onClick={() => {
                          approveRequest(request);
                        }}
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-sm btn-reject"
                        onClick={() => {
                          rejectRequest(request);
                        }}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                ) : userType === "monazam" ? (
                  <td className="tabledata-txt">
                    {request.status === "rejected" ? (
                      <button 
                        className="btn btn-sm btn-reject" 
                        disabled
                      >
                        Rejected
                      </button>
                    ) : (
                      <button 
                        className="btn btn-sm btn-approved" 
                        disabled
                      >
                        Approved
                      </button>
                    )}
                  </td>
                ) : (
                  <td className="tabledata-txt">
                    <span className={`status-badge ${request.status === "approved" ? "status-done" : "status-pending"}`}>
                      {request.status === "approved" ? "Done" : "Pending"}
                    </span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        )}
      </div>
    );
};
export default Table;
